import Tooltip from '@material-ui/core/Tooltip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Select from 'react-select';

import FilePreviewer from '@components/FilePreviewer';
import LoaderComponent from '@components/LoaderComponent';
import OrderStyleItem from '@components/OrderStyleItem';
import { toastError, toastSuccess } from '@components/Toast';

import DesignsByCollection from '@features/order/DesignsByCollection';

import { fetchDefaultQuotes } from '@store/orders/actions';
import { getDefaultQuotes } from '@store/reducers';

import { fetchEnumValues, findAllUsersByUserTypes } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { getShortName, onUploadFile } from '@utils/Util';

const TAB_KEY_CODE = 9;

const CreateOrder = () => {
	const [title, setTitle] = useState('');
	const [show, setShow] = useState(false);
	const [buyerId, setBuyerId] = useState('');
	const [dueDate, setDueDate] = useState('');
	const [poNumber, setPoNumber] = useState('');
	const [loading, setLoading] = useState(false);
	const [buyerList, setBuyerList] = useState([]);
	const [startDate, setStartDate] = useState('');
	const [orderBuyer, setOrderBuyer] = useState('');
	const [, setSearchBuyerLoading] = useState(false);
	const [poDocIdList, setPoDocIdList] = useState([]);
	const [orderStyles, setOrderStyles] = useState([]);
	const [collectionId, setCollectionId] = useState('');
	const [salesChannels, setSalesChannels] = useState([]);
	const [projectManagers, setProjectManagers] = useState([]);
	const [isPlacingOrder, setIsPlacingOrder] = useState(false);
	const [openStyleModal, setOpenStyleModal] = useState(false);
	const [selectedSalesChannel, setSelectedSalesChannel] = useState('');
	const [showBuyerSuggestion, setShowBuyerSuggestion] = useState(false);
	const [selectedProjectManager, setSelectedProjectManager] = useState('');
	const [errors, setErrors] = useState({
		orderTitleError: '',
		orderDeliveryDateError: '',
		orderStartDateError: '',
		orderBuyerIdError: '',
		salesChannelError: '',
		projectManagerError: ''
	});
	const [errorDesign, setErrorDesign] = useState({});
	const [searchParams] = useSearchParams();
	const idFromUrl = searchParams.get('id');

	const BuyerEmailRef = useRef(null);
	const resultsRef = useRef();
	const buttonRef = useRef();
	const inputRef = useRef();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const orderInfoRef = useRef(null);

	useEffect(() => {
		(async () => {
			try {
				await fetchEnumValues('info.nitex.app.common.enums.OrderStream', setSalesChannels);
				const data = await findAllUsersByUserTypes(`?userTypes=PROJECT_MANAGER`);
				setProjectManagers(
					data.PROJECT_MANAGER?.map((item) => ({ label: item.name, value: item.id }))
				);
			} catch {
				/* empty */
			}
		})();
	}, []);

	const getProductIds = (idList) => {
		return idList?.map((item) => item.productId);
	};

	const productIds = useMemo(() => getProductIds(orderStyles), [orderStyles]);

	let defaultQuotes = useSelector(getDefaultQuotes);
	const handleClickOutside = (event) => {
		if (BuyerEmailRef.current && !BuyerEmailRef.current.contains(event.target)) {
			setShowBuyerSuggestion(false);
		}
	};

	useEffect(() => {
		const orderCollectionId = searchParams.get('collectionId');
		setCollectionId(orderCollectionId);
		if (idFromUrl !== null) dispatch(fetchDefaultQuotes(idFromUrl.split(',')));
	}, [idFromUrl]);

	useEffect(() => {
		setOrderStyles(
			defaultQuotes.map((item) => {
				return {
					...item,
					priceType: item?.priceType || 'FOB'
				};
			})
		);
		document.addEventListener('click', handleClickOutside, true);
		return () => {
			document.removeEventListener('click', handleClickOutside, true);
		};
	}, [defaultQuotes]);

	const onSetOrderStyles = (data) => {
		setOrderStyles(data);
	};

	const getBuyerSuggestions = async (value) => {
		setSearchBuyerLoading(true);
		const brandId = searchParams.get('brandId');
		let params = `?organizationType=BUYER&email=${value}&isNitexUser=${false}&brandId=${brandId}`;
		await ApiClient.GET('getUserSuggestions', params)
			.then(({ data }) => {
				setSearchBuyerLoading(false);
				if (data) {
					setBuyerList(data);
				}
			})
			.catch(({ response }) => {
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const setBuyer = (user) => {
		setOrderBuyer(user.email);
		setBuyerId(user.id);
		setShowBuyerSuggestion(false);
	};

	const onChangeOrder = async (e) => {
		inputRef.current.focus();
		setErrors({
			...errors,
			orderTitleError: '',
			orderDeliveryDateError: '',
			orderStartDateError: '',
			salesChannelError: '',
			projectManagerError: '',
			orderBuyerIdError: ''
		});
		let { name, value } = e.target;
		if (name === 'orderBuyer') {
			setOrderBuyer(value);
			getBuyerSuggestions(value);
		}
	};

	const onUploadPo = async (poData) => {
		setLoading(true);
		await ApiClient.POST('uploadDocument', poData)
			.then(({ data }) => {
				setLoading(false);
				if (data) {
					setPoDocIdList((prev) => [
						...prev,
						{ name: poData.name, id: data.id, docUrl: data.docUrl }
					]);
					toastSuccess('Uploaded successfully');
				}
			})
			.catch(({ response }) => {
				setLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const getTotal = (items) => {
		return items.reduce((total, pair) => {
			if (pair.quantity) {
				return total + parseInt(pair.quantity);
			}
			return total;
		}, 0);
	};

	const getColorWisePrice = (colorWisePrices, id) => {
		if (colorWisePrices[id]) {
			return colorWisePrices[id];
		}
		return null;
	};

	const resetValidation = (fieldName, value) => {
		let isDataExist;
		if (Array.isArray(value)) {
			isDataExist = fieldName !== undefined && value.length > 0;
		} else {
			isDataExist = fieldName !== undefined && value.toString().length > 0;
		}

		if (isDataExist) {
			switch (fieldName) {
				case 'title':
					removeErrorFlag('orderTitleError');
					break;
				case 'buyerId':
					removeErrorFlag('orderBuyerIdError');
					break;
				case 'poNumber':
					removeErrorFlag('orderPoError');
					break;
				case 'startDate':
					removeErrorFlag('orderStartDateError');
					break;
				case 'salesChannel':
					removeErrorFlag('salesChannelError');
					break;
				case 'projectManager':
					removeErrorFlag('projectManagerError');
					break;
				case 'dueDate':
					removeErrorFlag('orderDeliveryDateError');
					break;
				default:
					break;
			}
		}
	};

	const removeErrorFlag = (flagName) => {
		let errorObj = { ...errors };
		if (flagName in errorObj) {
			errorObj[flagName] = undefined;
		}
		setErrors(errorObj);
	};

	const onSave = () => {
		if (!title) {
			setErrors({ orderTitleError: 'Insert an order title' });
			orderInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!selectedProjectManager?.value) {
			setErrors({ projectManagerError: 'Select project manager' });
			orderInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!buyerId) {
			setErrors({ orderBuyerIdError: 'Select a buyer' });
			orderInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!selectedSalesChannel?.value) {
			setErrors({ salesChannelError: 'Select revenue source' });
			orderInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!startDate) {
			setErrors({ orderStartDateError: 'Select order receive date' });
			orderInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return;
		}
		if (!dueDate) {
			setErrors({ orderDeliveryDateError: 'Select estimated delivery date' });
			orderInfoRef.current.scrollIntoView({ behavior: 'smooth' });
			return;
		}

		let isValid = true;
		orderStyles
			.slice()
			.reverse()
			.forEach((item) => {
				if (!item.referenceNumber) {
					setErrorDesign({ designNoError: 'Design number is required', designId: item.id });
					isValid = false;
				}
				if (item.poNumber === '') {
					setErrorDesign({ poError: 'PO number is required', designId: item.id });
					isValid = false;
				}
				if (item.poReceiveDate === '' || !item.poReceiveDate) {
					setErrorDesign({ poDateError: 'PO received date is required', designId: item.id });
					isValid = false;
				}
				if (item.deliveryDate === '') {
					setErrorDesign({ etdError: 'Delivery date is required', designId: item.id });
					isValid = false;
				}
				if (item.priceType === '') {
					setErrorDesign({ priceTypeError: 'Price type is required', designId: item.id });
					isValid = false;
				}
				if (
					item.buyerQuotationType === 'DESIGNWISE' &&
					(!item.designWiseBuyerPrice || isNaN(item.designWiseBuyerPrice))
				) {
					setErrorDesign({
						designWiseBuyerPriceError: 'Unit price is required',
						designId: item.id
					});
					isValid = false;
				}
				if (item.buyerQuotationType === 'COLORWISE') {
					let itemTotal = 0;
					item.colorWiseSizeQuantityPairList.map((value, index) => {
						itemTotal = getTotal(value.sizeQuantityPairList);
						let price = getColorWisePrice(item.colorWiseBuyerPrice, value.id);
						if (itemTotal > 0 && price == null) {
							setErrorDesign({
								colorWisePriceError: 'Color wise price is required',
								designId: item.id,
								colorIndex: index
							});
							isValid = false;
						}
					});
				}

				if (item.buyerQuotationType === 'SIZEWISE') {
					let sizeWiseTotal = {};
					item.colorWiseSizeQuantityPairList.map((colorObject) => {
						colorObject.sizeQuantityPairList.map((sizeObject) => {
							if (!sizeWiseTotal[sizeObject.id])
								sizeWiseTotal[sizeObject.id] = sizeObject.quantity;
							else
								sizeWiseTotal[sizeObject.id] =
									sizeWiseTotal[sizeObject.id] + sizeObject.quantity;
						});
					});
					for (let key in sizeWiseTotal) {
						if (sizeWiseTotal[key] && sizeWiseTotal[key] > 0 && !item.sizeWiseBuyerPrice[key]) {
							setErrorDesign({
								errorMessage: 'Price is required',
								designId: item.id,
								sizeKey: key
							});
							isValid = false;
						}
					}
				}

				if (!item.total || item.total === 0) {
					setErrorDesign({
						totalError: 'At least one quantity is required',
						designId: item.id
					});
					isValid = false;
				}
			});

		if (!isValid) return;
		setErrorDesign({});
		setShow(true);
	};

	const onPlaceOrder = async () => {
		setIsPlacingOrder(true);
		let body = {
			buyerId: buyerId,
			collectionId,
			dueDate,
			startDate,
			title,
			poNumber,
			productWiseQuantityPriceList: orderStyles,
			poDocIdList: poDocIdList.map((item) => item.id),
			projectManagerId: selectedProjectManager?.value
		};
		if (selectedSalesChannel?.value) {
			body['stream'] = selectedSalesChannel.value;
		}
		await ApiClient.POST('placeOrderFromCosting', body)
			.then(({ data }) => {
				setIsPlacingOrder(false);
				handleClose();
				if (data.success) {
					toastSuccess(data.message);
					navigate('/orders/my-orders?active=PENDING');
				} else {
					toastError(data.message);
				}
			})
			.catch(({ response }) => {
				setIsPlacingOrder(false);
				handleClose();
				if (response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};

	const onPoNumberChange = (e) => {
		setPoNumber(e.target.value);
		orderStyles.forEach((item) => {
			item['poNumber'] = e.target.value;
		});
		resetValidation('poNumber', e.target.value);
	};
	const onDueDateChange = (e) => {
		setDueDate(e.target.value);
		orderStyles.forEach((item) => {
			item['deliveryDate'] = e.target.value;
		});
		resetValidation('dueDate', e.target.value);
	};

	const onPoReceiveDateChange = (e) => {
		setStartDate(e.target.value);
		orderStyles.forEach((item) => {
			item['poReceiveDate'] = e.target.value;
		});
		resetValidation('startDate', e.target.value);
	};

	const getDesignWiseBuyerPrice = (price) => {
		if (isNaN(price)) {
			return null;
		}
		return price;
	};

	const handleClose = () => {
		setShow(false);
	};

	const getTotalUnits = () => {
		let total = 0;
		orderStyles.forEach((item) => {
			if (item.buyerQuotationType === 'DESIGNWISE') {
				item.colorWiseSizeQuantityPairList.map((value) => {
					total = total + getTotal(value.sizeQuantityPairList);
				});
			}
			if (item.buyerQuotationType === 'COLORWISE') {
				let itemTotal = 0;
				item.colorWiseSizeQuantityPairList.map((value) => {
					itemTotal = getTotal(value.sizeQuantityPairList);
					total = total + itemTotal;
				});
			}
			if (item.buyerQuotationType === 'SIZEWISE') {
				let sizeWiseTotal = {};
				item.colorWiseSizeQuantityPairList.map((colorObject) => {
					colorObject.sizeQuantityPairList.map((sizeObject) => {
						if (!sizeWiseTotal[sizeObject.id]) {
							sizeWiseTotal[sizeObject.id] = sizeObject.quantity;
						} else {
							sizeWiseTotal[sizeObject.id] = sizeWiseTotal[sizeObject.id] + sizeObject.quantity;
						}
					});
				});

				for (let key in sizeWiseTotal) {
					if (sizeWiseTotal[key]) {
						total = total + sizeWiseTotal[key];
					}
				}
			}
		});
		return total;
	};

	const getTotalValues = () => {
		let total = 0;
		orderStyles.forEach((item) => {
			if (item.buyerQuotationType === 'DESIGNWISE') {
				item.colorWiseSizeQuantityPairList.map((value) => {
					total =
						total +
						getTotal(value.sizeQuantityPairList) *
							getDesignWiseBuyerPrice(item.designWiseBuyerPrice);
				});
			}
			if (item.buyerQuotationType === 'COLORWISE') {
				let itemTotal = 0;
				let price;
				item.colorWiseSizeQuantityPairList.map((value) => {
					itemTotal = getTotal(value.sizeQuantityPairList);
					price = getColorWisePrice(item.colorWiseBuyerPrice, value.id);
					total = total + itemTotal * price;
				});
			}
			if (item.buyerQuotationType === 'SIZEWISE') {
				let price;
				let sizeWiseTotal = {};
				item.colorWiseSizeQuantityPairList.map((colorObject) => {
					colorObject.sizeQuantityPairList.map((sizeObject) => {
						if (!sizeWiseTotal[sizeObject.id]) {
							sizeWiseTotal[sizeObject.id] = sizeObject.quantity;
						} else {
							sizeWiseTotal[sizeObject.id] = sizeWiseTotal[sizeObject.id] + sizeObject.quantity;
						}
					});
				});

				for (let key in sizeWiseTotal) {
					if (sizeWiseTotal[key] > 0 && item.sizeWiseBuyerPrice[key]) {
						price = item.sizeWiseBuyerPrice[key];
						total = total + sizeWiseTotal[key] * price;
					}
				}
			}
		});
		return Number.isInteger(total) ? total : parseFloat(total).toFixed(2);
	};

	const renderColorWiseTotalPrice = (productId) => {
		let total = 0;
		let itemTotal = 0;
		let price;
		let item = orderStyles.find((design) => parseInt(design.id) === parseInt(productId));
		item.colorWiseSizeQuantityPairList.map((value) => {
			itemTotal = getTotal(value.sizeQuantityPairList);
			price = getColorWisePrice(item.colorWiseBuyerPrice, value.id);
			total = total + itemTotal * price;
		});
		return isNaN(total.toFixed(2)) ? 0 : total.toFixed(2);
	};

	const renderSizeWiseTotalPrice = (productId) => {
		let price;
		let total = 0;
		let sizeWiseTotal = {};
		let item = orderStyles.find((design) => parseInt(design.id) === parseInt(productId));
		item.colorWiseSizeQuantityPairList.map((colorObject) => {
			colorObject.sizeQuantityPairList.map((sizeObject) => {
				if (!sizeWiseTotal[sizeObject.id]) {
					sizeWiseTotal[sizeObject.id] = sizeObject.quantity;
				} else {
					sizeWiseTotal[sizeObject.id] = sizeWiseTotal[sizeObject.id] + sizeObject.quantity;
				}
			});
		});

		for (let key in sizeWiseTotal) {
			if (sizeWiseTotal[key] > 0 && item.sizeWiseBuyerPrice[key]) {
				price = item.sizeWiseBuyerPrice[key];
				total = total + sizeWiseTotal[key] * price;
			}
		}
		return isNaN(total.toFixed(2)) ? 0 : total.toFixed(2);
	};

	const renderDesignWiseTotalPrice = (productId) => {
		let total = 0;
		let item = orderStyles.find((design) => parseInt(design.id) === parseInt(productId));
		item.colorWiseSizeQuantityPairList.map((value) => {
			total =
				total +
				getTotal(value.sizeQuantityPairList) * getDesignWiseBuyerPrice(item.designWiseBuyerPrice);
		});
		return isNaN(total.toFixed(2)) ? '0.00' : total.toFixed(2);
	};

	const renderColorWiseTotalUnits = (productId) => {
		let total = 0;
		let itemTotal = 0;
		let item = orderStyles.find((design) => parseInt(design.id) === parseInt(productId));
		item.colorWiseSizeQuantityPairList.map((value) => {
			itemTotal = getTotal(value.sizeQuantityPairList);
			total = total + itemTotal;
		});
		return isNaN(total) ? 0 : total;
	};

	const renderSizeWiseTotalUnits = (productId) => {
		let total = 0;
		let sizeWiseTotal = {};
		let item = orderStyles.find((design) => parseInt(design.id) === parseInt(productId));
		item.colorWiseSizeQuantityPairList.map((colorObject) => {
			colorObject.sizeQuantityPairList.map((sizeObject) => {
				if (!sizeWiseTotal[sizeObject.id]) {
					sizeWiseTotal[sizeObject.id] = sizeObject.quantity;
				} else {
					sizeWiseTotal[sizeObject.id] = sizeWiseTotal[sizeObject.id] + sizeObject.quantity;
				}
			});
		});

		for (let key in sizeWiseTotal) {
			if (sizeWiseTotal[key]) {
				total = total + sizeWiseTotal[key];
			}
		}
		return isNaN(total) ? 0 : total;
	};

	const renderDesignWiseTotalUnits = (productId) => {
		let total = 0;
		let item = orderStyles.find((design) => parseInt(design.id) === parseInt(productId));
		item.colorWiseSizeQuantityPairList.map((value) => {
			total = total + getTotal(value.sizeQuantityPairList);
		});
		return isNaN(total) ? 0 : total;
	};

	const onGetDesignWiseToalPrice = (id, designType) => {
		if (designType === 'COLORWISE') {
			return renderColorWiseTotalPrice(id);
		} else if (designType === 'SIZEWISE') {
			return renderSizeWiseTotalPrice(id);
		} else return renderDesignWiseTotalPrice(id);
	};

	const onGetDesignWiseToalQuantity = (id, designType) => {
		if (designType === 'COLORWISE') {
			return renderColorWiseTotalUnits(id);
		} else if (designType === 'SIZEWISE') {
			return renderSizeWiseTotalUnits(id);
		} else return renderDesignWiseTotalUnits(id);
	};

	const onRemovePo = async (itemId) => {
		const updatedList = poDocIdList.filter((item) => item.id !== itemId);
		setPoDocIdList(updatedList);
	};

	const onKeyDownPress = (e) => {
		if (e.keyCode === TAB_KEY_CODE) {
			setShowBuyerSuggestion(true);
		}
	};

	const addNewStyle = (product) => {
		setOrderStyles((prevState) => [...prevState, ...product]);
	};

	const renderRightSidePanel = () => {
		return (
			<DesignsByCollection
				addNewStyle={addNewStyle}
				stylesData={productIds}
				setOpenStyleModal={setOpenStyleModal}
			/>
		);
	};

	const removeStyle = (productId) => {
		setOrderStyles(orderStyles.filter((item) => item.productId !== productId));
	};

	useEffect(() => {
		if (openStyleModal) {
			buttonRef.current.click();
		}
	}, [openStyleModal]);

	return (
		<LoaderComponent loading={loading}>
			<div className="create-order-container height-without-header">
				<div className="page-title pb-3" ref={orderInfoRef}>
					<h3 className="semibold-16 mb-0">Create order</h3>
				</div>
				{/* Order Basic Info  */}
				<div className="order-basic-info-section">
					<div className="form-title">
						<h3 className="semibold-14">Order basic information*</h3>
						<div className="info-and-po d-flex justify-content-between">
							<div className="info-column bg-white">
								<div className="container px-0">
									<div className="row">
										<div className="col-md-8">
											<div className="form-group">
												<label htmlFor="oder-title">Order title</label>
												<input
													type="text"
													autoComplete="off"
													id="oder-title"
													name="title"
													value={title}
													onChange={(e) => {
														setTitle(e.target.value);
														resetValidation('title', e.target.value);
													}}
													onKeyDown={onKeyDownPress}
												/>
												{errors.orderTitleError && (
													<span className="error">{errors.orderTitleError}</span>
												)}
											</div>
										</div>
										<div className="col-md-4">
											<div className="form-group no-shadow-react-select">
												<label htmlFor="po-number">Project manager</label>
												<Select
													onChange={(selected) => {
														setSelectedProjectManager({
															label: selected.label,
															value: selected.value
														});
														resetValidation('projectManager', selected.value);
													}}
													value={selectedProjectManager}
													name="projectManager"
													isMulti={false}
													options={projectManagers}
												/>
												{errors.projectManagerError && (
													<span className="error">
														{errors.projectManagerError}
													</span>
												)}
											</div>
										</div>
										<div className="col-md-4">
											<div className="form-group">
												<label>Buyer</label>
												<input
													ref={inputRef}
													className={'bg-gray-light border-0'}
													type="text"
													placeholder="Search with buyer E-mail"
													value={orderBuyer}
													name="orderBuyer"
													onFocus={onChangeOrder}
													onChange={onChangeOrder}
													onClick={() => setShowBuyerSuggestion(true)}
													autoComplete="off"
												/>
												{errors.orderBuyerIdError && (
													<span className="error">{errors.orderBuyerIdError}</span>
												)}
												{showBuyerSuggestion ? (
													<div className="suggest-filed" ref={BuyerEmailRef}>
														<ul ref={resultsRef}>
															{buyerList.length ? (
																buyerList.map((item) => (
																	<li
																		key={item.id}
																		onClick={() => setBuyer(item)}
																		tabIndex={0}
																		onKeyPress={(e) => {
																			if (e.key === 'Enter') {
																				setBuyer(item);
																			}
																		}}
																	>
																		{item.email}
																	</li>
																))
															) : (
																<li
																	onClick={() =>
																		setShowBuyerSuggestion(false)
																	}
																>
																	No suggestions found
																</li>
															)}
														</ul>
													</div>
												) : (
													<></>
												)}
											</div>
										</div>
										<div className="col-md-4">
											<div className="form-group">
												<label htmlFor="po-number">PO number</label>
												<input
													type="text"
													autoComplete="off"
													id="po-number"
													name="poNumber"
													value={poNumber}
													onChange={(e) => onPoNumberChange(e)}
												/>
											</div>
										</div>
										<div className="col-md-4">
											<div className="form-group no-shadow-react-select">
												<label htmlFor="po-number">Revenue Stream</label>
												<Select
													onChange={(selected) => {
														setSelectedSalesChannel({
															label: selected.label,
															value: selected.value
														});
														resetValidation('salesChannel', selected.value);
													}}
													value={selectedSalesChannel}
													name="salesChannel"
													isMulti={false}
													options={salesChannels}
												/>
												{errors.salesChannelError && (
													<span className="error">{errors.salesChannelError}</span>
												)}
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group mb-0">
												<label htmlFor="po-receive">
													PO receive date (mm/dd/yyyy)
												</label>
												<input
													type="date"
													id="po-receive"
													name="startDate"
													value={startDate}
													onChange={(e) => onPoReceiveDateChange(e)}
												/>
												{errors.orderStartDateError && (
													<span className="error">
														{errors.orderStartDateError}
													</span>
												)}
											</div>
										</div>
										<div className="col-md-6">
											<div className="form-group mb-0">
												<label htmlFor="etd">
													Estimated Time of Delivery (ETD) (mm/dd/yyyy)
												</label>
												<input
													type="date"
													id="etd"
													name="dueDate"
													value={dueDate}
													onChange={(e) => onDueDateChange(e)}
												/>
												{errors.orderDeliveryDateError && (
													<span className="error">
														{errors.orderDeliveryDateError}
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="po-column bg-white">
								<div className="uploded-po pt-2">
									{poDocIdList.map((item) => (
										<div
											className="single-po d-flex justify-content-between align-items-center"
											key={item.id}
										>
											<div className="po-names">
												<FilePreviewer docUrl={item.docUrl} />
												<Tooltip title={item.name} placement="top" arrow>
													<a
														href={item.docUrl}
														target="_blank"
														rel="noopener noreferrer"
													>
														{getShortName(item.name, 15)}
													</a>
												</Tooltip>
											</div>
											<div className="delete-btn" onClick={() => onRemovePo(item.id)}>
												<img
													src={`${process.env.PUBLIC_URL}/icons/delete_gray.svg`}
													alt="delete"
												/>
											</div>
										</div>
									))}
									{(!poDocIdList || poDocIdList.length === 0) && (
										<div>
											<p className="regular-14 text-center no-po">No PO uploaded</p>
										</div>
									)}
								</div>

								<div className="upload-po text-center">
									<label htmlFor="po-upload" className="drag-upload">
										<img
											src={`${process.env.PUBLIC_URL}/icons/upload-sm.svg`}
											alt="Upload po"
										/>{' '}
										<span>Upload PO</span>
									</label>
									<input
										type="file"
										className="file-upload d-none"
										onChange={(e) =>
											onUploadFile(e, 'PURCHASE_ORDER', onUploadPo, 'MULTIPLE')
										}
										onClick={(e) => (e.target.value = null)}
										name="po-upload"
										id="po-upload"
										multiple
										accept="*"
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="designs-info-section">
					{/* Design info section  */}
					<h3 className="semibold-14">Design info</h3>
					<OrderStyleItem
						onSetOrderStyles={onSetOrderStyles}
						stylesData={orderStyles}
						errorInfo={errorDesign}
						onGetDesignWiseToalPrice={onGetDesignWiseToalPrice}
						onGetDesignWiseToalQuantity={onGetDesignWiseToalQuantity}
						isFromCreateOrder={true}
						removeStyle={removeStyle}
					/>
					{openStyleModal && renderRightSidePanel()}
					<div className="submit-button pb-3 text-center">
						<button
							className="button gray-outline size32 ml-2"
							onClick={() => setOpenStyleModal(true)}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/plus-20.svg`} alt="add" />
							<span className="ml-1 gray_dark_01">Style</span>
						</button>
						<button
							className={'button gray-outline size32 ml-2 d-none'}
							data-toggle="modal"
							data-target="#add-multiple-design"
							ref={buttonRef}
						>
							<img src={`${process.env.PUBLIC_URL}/icons/plus-20.svg`} alt="add" />
							<span className="ml-1 gray_dark_01">Style</span>
						</button>
						{orderStyles.length > 0 && (
							<button className="button size32 ml-2 float-right" onClick={onSave}>
								Submit
							</button>
						)}
					</div>
				</div>
			</div>
			<Modal show={show} onHide={handleClose} centered>
				<LoaderComponent loading={isPlacingOrder}>
					<div className="common-popup placing-order-popup shadow-24dp">
						<Modal.Header closeButton>
							<h3 className="semibold-16 mb-0">Are you sure about placing the order?</h3>
						</Modal.Header>
						<Modal.Body>
							<div className="common-popup-body">
								<div className="order-summary">
									<div className="summary-heading">
										<h3>Order summary</h3>
									</div>
									<div className="summary-details d-flex justify-content-between">
										<div className="one-third">
											<h3>{orderStyles.length}</h3>
											<p className="regular-14 gray_dark_02">
												{orderStyles.length === 1 ? 'Design' : 'Designs'}
											</p>
										</div>
										<div className="one-third">
											<h3>{getTotalUnits()}</h3>
											<p className="regular-14 gray_dark_02">Unit</p>
										</div>
										<div className="one-third">
											<h3>${getTotalValues()}</h3>
											<p className="regular-14 gray_dark_02">Value</p>
										</div>
									</div>
								</div>
							</div>
						</Modal.Body>
						<Modal.Footer>
							<div className="footer-buttons text-right">
								<button className="button outline size32" onClick={() => handleClose()}>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									disabled={isPlacingOrder}
									onClick={() => onPlaceOrder()}
								>
									Place order
								</button>
							</div>
						</Modal.Footer>
					</div>{' '}
				</LoaderComponent>
			</Modal>
		</LoaderComponent>
	);
};

export default CreateOrder;
